import { ArrowRight, Zap } from 'lucide-react-native';
import { ImageBackground, Pressable, StyleSheet, View } from 'react-native';

import { Avatar } from '@/components/Avatar';
import { DealAvailabilityBar } from '@/components/AvailabilityBar';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Pill } from '@/components/Pill';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import type { Deal } from '@/types/deal';

// Matches DealHeroCard's mark, so tapping through to the deal detail doesn't
// resize the brand. 72 was tried and rejected — a Frost tile that big
// out-shouts the headline, which is the actual offer.
const LOGO_SIZE = 48;

export type FeaturedDealCardProps = {
  deal: Deal;
  onPress: () => void;
  testID?: string;
};

/**
 * Home's pinned featured-deal slot — the surface Season of Gifting proved out,
 * repurposed as an evergreen funnel. Which deal lands here is
 * `dealsStore.featuredDeal`; this component renders the one it's given.
 *
 * ONE Deep Green surface, per the handoff (`screens/home.jsx:122–172`) and
 * `docs/DESIGN.md:173`. A light-bodied version was built first and rejected on
 * the sim: splitting it into a dark band over a white body put a hard seam
 * across the middle, left the lower half mostly empty, and orphaned the CTA in
 * a corner — it read as a list row with a header glued on. The dark card is
 * also what lets the Electric Green CTA do its job; that combo (Deep Green +
 * Frost + Electric Green, brand kit p.10) is the brand's signature and it's
 * wasted on a white body.
 *
 * It does sit directly under the dark balance hero. They stay distinct through
 * texture rather than surface color: the hero is a giant numeric + MP
 * watermark, this is a brand lockup + headline + pill. The watermark is
 * deliberately NOT repeated here — two scaled MPs stacked is noise, and
 * `HeroWatermark`'s 200pt geometry is tuned for the taller hero anyway.
 *
 * No `MoneyDisplay`: deal titles already lead with the amount ("GET $110 WHEN
 * YOU OPEN…"), and `DealHeroCard` already found a second dollar figure reads
 * as redundant.
 */
export function FeaturedDealCard({ deal, onPress, testID }: FeaturedDealCardProps) {
  const initials = deal.brand_name.slice(0, 2).toUpperCase();

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Featured deal: ${deal.display_name} — ${deal.title}`}
      testID={testID}
    >
      {({ pressed }) => (
        <CardSurface uri={deal.cover_image_url} pressed={pressed}>
          <View style={styles.brandRow}>
            <BrandLogo
              url={deal.brand_logo_url}
              size={LOGO_SIZE}
              fallback={<Avatar initials={initials} size={LOGO_SIZE} tone="electric" />}
              testID="featured-deal-logo"
            />
            <View style={styles.brandText}>
              <Text variant="bodyBold" color="frost" numberOfLines={1}>
                {deal.display_name}
              </Text>
              <Overline tone="onDark">{deal.brand_category}</Overline>
            </View>
            <Pill tone="green" icon={Zap}>
              Featured
            </Pill>
          </View>

          {/* Scarcity sits with the brand, not above the CTA. Below the
              headline it landed ~60px from the Electric Green button — two
              full-width green horizontals stacked, so the bar read as a
              progress meter for the button. Here it works as a subhead to the
              lockup and the greens stay apart.
              `compact` is required, not cosmetic: it drops the bar's
              interactive explainer, which would nest a Pressable inside this
              card's Pressable. An uncapped deal gets the same-height
              "No monthly limit" bar. */}
          <DealAvailabilityBar deal={deal} tone="dark" compact style={styles.bar} />

          <Heading size="2xl" tone="inverse" style={styles.title}>
            {deal.title}
          </Heading>

          {/* Presentational only — the whole card is the tap target, so this
              must not become a second one (the handoff does the same with
              `pointerEvents: 'none'`). Hidden from the a11y tree too, or
              VoiceOver announces a "Start Deal" button that isn't focusable,
              on top of the card's own label.

              `aria-hidden` rather than the iOS/Android prop pair: RN maps it
              to both (accessibilityElementsHidden +
              importantForAccessibility="no-hide-descendants", View.js), while
              react-native-web forwards only `aria-hidden` and drops the other
              two entirely. `Button` is a Pressable, so on web those dropped
              props left a focusable, screen-reader-visible "Start Deal" that
              `pointerEvents: 'none'` still made a dead tab stop. */}
          <View pointerEvents="none" style={styles.cta} aria-hidden>
            <Button label="Start Deal" trailingIcon={ArrowRight} fullWidth />
          </View>
        </CardSurface>
      )}
    </Pressable>
  );
}

/**
 * The card's single Deep Green surface. Renders `cover_image_url` behind the
 * content when a deal has one — none do today, so in practice this is the
 * solid card. Keeping both paths on one layout means adding a photo later
 * can't shift the card's geometry.
 */
function CardSurface({
  uri,
  pressed,
  children,
}: {
  uri: string | null;
  pressed: boolean;
  children: React.ReactNode;
}) {
  const style = [styles.card, pressed && styles.cardPressed];

  if (!uri) {
    return (
      <Card variant="dark" padding={spacing.lg} style={style}>
        {children}
      </Card>
    );
  }
  return (
    <Card variant="dark" padding={0} style={style}>
      <ImageBackground source={{ uri }} style={styles.photo} imageStyle={styles.photoImage}>
        {/* Keeps a light photo from swallowing Frost text. Never mounts on the
            solid path. */}
        <View style={styles.photoScrim} />
        {children}
      </ImageBackground>
    </Card>
  );
}

const styles = StyleSheet.create({
  // Sits directly under the Deep Green balance hero, and two dark cards 24pt
  // apart merge into one mass against the Frost background. The Electric Green
  // hairline gives this card its own edge and doubles as "featured" chrome —
  // the hero has no border, so the treatment reads as a distinction rather
  // than decoration. Extra top margin adds the rest of the separation.
  card: {
    // Same corner as the balance hero directly above — they read as a pair,
    // and a 24 vs 28 mismatch on two stacked full-width cards is the kind of
    // 4pt drift you feel without being able to name.
    borderRadius: radii['2xl'],
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.electricGreen,
    marginTop: spacing.sm,
  },
  cardPressed: {
    opacity: 0.9,
  },
  photo: {
    padding: spacing.lg,
  },
  photoImage: {
    borderRadius: radii['2xl'],
  },
  photoScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.deepGreenOverlay,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  brandText: {
    flex: 1,
    gap: spacing.xs / 2,
  },
  title: {
    marginTop: spacing.lg,
  },
  bar: {
    marginTop: spacing.md,
  },
  cta: {
    marginTop: spacing.lg,
  },
});
